import { createSelector } from '@reduxjs/toolkit';
import { IIncomeForm } from 'components/IncomeCalculator/types';
import { IColumn, TableData } from 'modules/core/components/Table/types';
import { RETAIL_NAME } from 'modules/core/constants/report.const';
import {
  IAvailableFilters,
  IFeesAndTaxes,
  IFetchingVehicleFlags,
  ILenderVehicle,
  ITableViewReport,
} from 'modules/core/types';
import { toIncomeCalculatorReportData } from 'modules/core/utils/selectors/incomeReport';
import { ILenderForm, ITier } from 'modules/Lender/types/Lender.types';
import { CreditScoreColors } from 'modules/ProbabilityEstimator/enums/creditScoreColors.enum';
import { MINIMAL_PAYMENT_RANGE } from 'modules/ProbabilityEstimator/probabilityEstimator.const';
import { ProbabilityEstimatorCalculationService } from 'modules/ProbabilityEstimator/services';
import { ILendersState, ILenderState, initialStateOneLender } from 'store/reducers/lenders.reducer';
import { RootState } from 'store/store';
import { PERMISSIONS } from 'modules/PermissionWrapper/permissions.const';
import { SSO_TYPE } from 'modules/core/constants';
import { IMenuLink } from 'modules/core/components/SideBar/sideBarMenuLinks';

import { getUser } from './auth.selector';
import { getFeesAndTaxesState } from './controls.selector';
import { getSelectedRows, getTableVisibleColumns } from './probabilityEstimator.selector';

const getLendersState = (state: RootState): Record<string, ILenderState> => state.lenders.lendersHashTable;

export const getLenderState = (state: RootState): ILenderState => getLendersState(state)[state.lenders.currentLenderId];

export const getCurrentLenderControls = (state: RootState): ILendersState['currentLenderControls'] =>
  state.lenders.currentLenderControls;

export const getLendersList = (state: RootState): IMenuLink[] =>
  Object.values(getLendersState(state)).map((lender) => ({
    name: lender.name,
    iconUrl: '',
    link: lender.path,
    allowedRoles: PERMISSIONS.LENDER,
    smallFont: true,
    isShopByBudgetEnabled: lender.isShopByBudgetEnabled,
    isShopByCallbackEnabled: lender.isShopByCallbackEnabled,
    isSalespersonAccess: lender.isSalespersonAccess,
    isBalloonSalespersonAccess: lender.isBalloonSalespersonAccess,
    withoutLenderRedirect: lender.withoutLenderRedirect,
    uiName: lender.uiName,
    routeOneId: lender.route_one_id ?? null,
  }));

export const getLendersFullIds = (state: RootState): string[] => Object.keys(getLendersState(state));

export const getCurrentLenderId = (state: RootState): string => state.lenders.currentLenderId;

export const getCurrentLenderDbId = (state: RootState): number | null => getLenderState(state)?.id ?? null;

export const getLenderInitialValues = (state: RootState): ILenderForm => getLenderState(state).initialValues;

export const getLenderInitialErrors = (state: RootState): Record<string, string> => getLenderState(state).initialErrors;

export const getFetchingLendersFlag = (state: RootState): boolean => state.lenders.isFetchingLendersList;

export const getFetchingLenderFlag = (state: RootState): boolean => state.lenders.isFetchingLender;

export const getLoadedLenderFlag = (state: RootState): boolean => state.lenders.isLoadedLender;

export const getLenderVehiclesByParams = (state: RootState): ILenderVehicle[] | undefined =>
  getLenderState(state).vehicles.vehiclesByParams;

export const getLenderVehiclesByStock = (state: RootState): ILenderVehicle[] | undefined =>
  getLenderState(state).vehicles.vehiclesByStock;

export const getAvailableFilters = (state: RootState): IAvailableFilters | undefined =>
  getLenderState(state).availableFilters;

export const getNotFoundVehicles = (state: RootState): string[] => getLenderState(state).notFoundVehicles;

export const getNotAvailableVehicles = (state: RootState): string[] => getLenderState(state).notAvailableVehicles;

export const getIsTableView = (state: RootState): boolean => getLenderState(state).isTableView;

export const getIncome = (state: RootState): IIncomeForm =>
  getLenderState(state)?.income || initialStateOneLender.income;

export const getIncomeCalculatorReportData = createSelector(getIncome, toIncomeCalculatorReportData);

export const getTiers = (state: RootState): ITier[] =>
  getLenderState(state).tiers.map((tier) => ({ ...tier, color: CreditScoreColors.GREEN })) || []; // TODO_AK remove after backend add color in SWA-4350 task

export const getFirstDealSourceRender = (state: RootState): boolean => getLenderState(state).firstDealSourceRender;

export const getIsTierLockedByRedirect = createSelector(getLenderState, getUser, (lender, user): boolean =>
  Boolean(
    lender?.lock_tier_after_redirect &&
      user?.is_sso &&
      user?.sso_type !== SSO_TYPE.DEALER &&
      !lender.firstDealSourceRender &&
      !lender.dealSourceTierEditable,
  ),
);

export const getDealSourceHighLtvLimit = (state: RootState): number | undefined =>
  getLenderState(state)?.dealSourceData?.finance_details?.high_ltv_limit;

export const getVehicleFetchingFlags = (state: RootState): IFetchingVehicleFlags => {
  const { isFetchingVehicles, isFetchingVehiclesFailed, resetCount, fetchVehiclesError } = getLenderState(state);

  return { isFetchingVehicles, isFetchingVehiclesFailed, resetCount, fetchVehiclesError };
};

export const getSortedVehicles = (state: RootState): ILenderVehicle[] =>
  getLenderState(state).vehicles.sortedVehicles || [];

const toLenderReportData = (
  lenderData: ILenderState,
  controlFeesAndTaxes: IFeesAndTaxes,
  tableSelectedRows: TableData[],
  tableVisibleColumns: IColumn[],
): ITableViewReport => {
  const {
    totalNetDownPaymentDialog,
    feesAndTaxes,
    tier: creditScore,
    grossMonthlyIncome,
    desiredMonthlyPayment,
    preferences,
  } = lenderData.initialValues;

  const maximalMonthlyPayment = ProbabilityEstimatorCalculationService.getMaximalMonthlyPayment(
    creditScore.max_allowed_pti,
    creditScore.max_allowed_payment,
    grossMonthlyIncome || 0,
  );

  return {
    disclaimer: {
      amountOwed: totalNetDownPaymentDialog.amountOwed,
      bankDiscount: feesAndTaxes.bankDiscount,
      bankFees: feesAndTaxes.bankFees,
      cashDown: totalNetDownPaymentDialog.cashDown,
      desiredMonthly: desiredMonthlyPayment || 0,
      desiredMonthlyCeil: maximalMonthlyPayment,
      desiredMonthlyFloor: MINIMAL_PAYMENT_RANGE,
      grossMonthlyIncome: grossMonthlyIncome || 0,
      isLender: true,
      ltvHighLimit: preferences.ltvHighLimit,
      salesTaxRate: feesAndTaxes.salesTaxRate || 0,
      salesFees: feesAndTaxes.estimatedFees || 0,
      selectedCreditScore: { ...creditScore, interest_rate: creditScore.rate },
      tradeValue: totalNetDownPaymentDialog.tradeValue,
    },
    type: RETAIL_NAME,
    vehicles: tableSelectedRows,
    vehicle_parameters: tableVisibleColumns,
  };
};

export const getLenderReportData = createSelector(
  getLenderState,
  getFeesAndTaxesState,
  getSelectedRows,
  getTableVisibleColumns,
  toLenderReportData,
);
